import React, { useState } from 'react';
import { leadsService } from '../services/api';

type Step = 'info' | 'business' | 'business_detail' | 'job_type' | 'success';

const JOB_OPTIONS = [
  { value: 'کارمند', label: 'کارمند' },
  { value: 'خانه‌دار', label: 'خانه‌دار' },
  { value: 'کارگر', label: 'کارگر' },
  { value: 'بیکار / کارجو / دانشجو', label: 'بیکار، کارجو یا دانشجو' },
];

const STAFF_OPTIONS = [
  '۱ تا ۵ نفر',
  '۶ تا ۲۰ نفر',
  '۲۱ تا ۵۰ نفر',
  'بیش از ۵۰ نفر',
];

export default function BookletLanding() {
  const [step, setStep] = useState<Step>('info');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [isBusinessOwner, setIsBusinessOwner] = useState<boolean | null>(null);
  const [staffCount, setStaffCount] = useState('');
  const [businessField, setBusinessField] = useState('');
  const [jobType, setJobType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submitLead = async (extra: {
    isBusinessOwner: boolean;
    staffCount?: string;
    businessField?: string;
    jobType?: string;
  }) => {
    setLoading(true);
    setError('');
    try {
      await leadsService.create({
        fullName: fullName.trim(),
        phone: phone.trim(),
        source: 'BOOKLET_LANDING',
        isBusinessOwner: extra.isBusinessOwner,
        staffCount: extra.staffCount,
        businessField: extra.businessField,
        jobType: extra.jobType,
      });
      setStep('success');
      const a = document.createElement('a');
      a.href = '/booklet.pdf';
      a.download = 'کتابچه_ثروتسازی.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'خطا در ثبت اطلاعات. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const handleInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('نام و نام خانوادگی را وارد کنید.'); return; }
    const digits = phone.trim().replace(/\D/g, '');
    if (!/^(09\d{9}|9\d{9})$/.test(digits)) { setError('شماره موبایل معتبر وارد کنید.'); return; }
    setError('');
    setStep('business');
  };

  const handleBusinessChoice = (choice: boolean) => {
    setIsBusinessOwner(choice);
    setStep(choice ? 'business_detail' : 'job_type');
  };

  const handleBusinessDetailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffCount) { setError('تعداد پرسنل را انتخاب کنید.'); return; }
    if (!businessField.trim()) { setError('حوزه کاری را وارد کنید.'); return; }
    setError('');
    submitLead({ isBusinessOwner: true, staffCount, businessField: businessField.trim() });
  };

  const handleJobTypeSubmit = (job: string) => {
    setJobType(job);
    submitLead({ isBusinessOwner: false, jobType: job });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] flex flex-col items-center justify-center px-4 py-10" dir="rtl">

      {/* Floating particles */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full opacity-20 animate-pulse"
            style={{
              width: `${Math.random() * 6 + 3}px`,
              height: `${Math.random() * 6 + 3}px`,
              background: i % 3 === 0 ? '#f59e0b' : i % 3 === 1 ? '#a78bfa' : '#34d399',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-md">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 shadow-lg shadow-amber-500/30">
            <span className="text-4xl">📖</span>
          </div>
          <h1 className="text-2xl font-black text-white leading-tight">
            کتابچه رایگان<br />
            <span className="text-amber-400">۱۰ باور کلیدی ثروت‌سازی</span>
          </h1>
          <p className="mt-2 text-sm text-purple-200">
            دریافت رایگان — فقط چند ثانیه طول می‌کشد
          </p>
        </div>

        {/* Progress bar */}
        {step !== 'success' && (
          <div className="mb-6 flex items-center gap-2">
            {(['info', 'business', step === 'business_detail' ? 'business_detail' : 'job_type'] as const).map((s, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                  (step === 'info' && idx === 0) ||
                  (step === 'business' && idx <= 1) ||
                  ((step === 'business_detail' || step === 'job_type') && idx <= 2)
                    ? 'bg-amber-400'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        )}

        {/* Card */}
        <div className="rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 p-7 shadow-2xl">

          {/* Step 1: Info */}
          {step === 'info' && (
            <form onSubmit={handleInfoSubmit} className="space-y-4">
              <div>
                <p className="mb-4 text-center text-base font-bold text-white">اطلاعات خود را وارد کنید</p>
                <label className="mb-1 block text-xs font-semibold text-purple-200">نام و نام خانوادگی</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => { setFullName(e.target.value); setError(''); }}
                  placeholder="مثال: علی احمدی"
                  className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-white/40 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-purple-200">شماره تماس</label>
                <input
                  type="tel"
                  dir="ltr"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  placeholder="09xxxxxxxxx"
                  className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-left text-white placeholder-white/40 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>
              {error && <p className="rounded-xl bg-red-500/20 px-3 py-2 text-xs font-semibold text-red-300">{error}</p>}
              <button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-l from-amber-500 to-yellow-400 px-4 py-3.5 text-sm font-black text-gray-900 shadow-lg shadow-amber-500/30 transition hover:opacity-90 active:scale-95"
              >
                ادامه →
              </button>
            </form>
          )}

          {/* Step 2: Business owner? */}
          {step === 'business' && (
            <div className="space-y-4">
              <p className="text-center text-base font-bold text-white">
                آیا صاحب کسب‌وکار هستی؟
              </p>
              <button
                onClick={() => handleBusinessChoice(true)}
                className="group w-full rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/10 px-5 py-4 text-right transition hover:border-emerald-400 hover:bg-emerald-500/20"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🏢</span>
                  <div>
                    <p className="font-black text-white">بله، صاحب کسب‌وکارم</p>
                    <p className="text-xs text-emerald-300">کارآفرین، مدیر یا کسب‌وکار مستقل دارم</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleBusinessChoice(false)}
                className="group w-full rounded-2xl border-2 border-purple-400/40 bg-purple-500/10 px-5 py-4 text-right transition hover:border-purple-400 hover:bg-purple-500/20"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👤</span>
                  <div>
                    <p className="font-black text-white">خیر</p>
                    <p className="text-xs text-purple-300">کارمند، خانه‌دار یا دانشجو هستم</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step 3a: Business detail */}
          {step === 'business_detail' && (
            <form onSubmit={handleBusinessDetailSubmit} className="space-y-4">
              <p className="text-center text-base font-bold text-white">کمی بیشتر بگو</p>
              <div>
                <label className="mb-1 block text-xs font-semibold text-purple-200">تعداد پرسنل</label>
                <div className="grid grid-cols-2 gap-2">
                  {STAFF_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => { setStaffCount(opt); setError(''); }}
                      className={`rounded-xl border py-3 text-xs font-bold transition ${
                        staffCount === opt
                          ? 'border-amber-400 bg-amber-400/20 text-amber-300'
                          : 'border-white/20 bg-white/5 text-white hover:bg-white/10'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-purple-200">حوزه کاری</label>
                <input
                  type="text"
                  value={businessField}
                  onChange={e => { setBusinessField(e.target.value); setError(''); }}
                  placeholder="مثال: فروش، ساختمان، آموزش..."
                  className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-white/40 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                />
              </div>
              {error && <p className="rounded-xl bg-red-500/20 px-3 py-2 text-xs font-semibold text-red-300">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-l from-amber-500 to-yellow-400 px-4 py-3.5 text-sm font-black text-gray-900 shadow-lg shadow-amber-500/30 transition hover:opacity-90 active:scale-95 disabled:opacity-60"
              >
                {loading ? 'در حال دریافت کتابچه...' : '📥 دریافت رایگان کتابچه'}
              </button>
            </form>
          )}

          {/* Step 3b: Job type */}
          {step === 'job_type' && (
            <div className="space-y-3">
              <p className="text-center text-base font-bold text-white">شغل خود را انتخاب کن</p>
              {JOB_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  disabled={loading}
                  onClick={() => handleJobTypeSubmit(opt.value)}
                  className="w-full rounded-2xl border border-white/20 bg-white/5 px-5 py-4 text-right font-bold text-white transition hover:border-purple-400 hover:bg-purple-500/15 active:scale-95 disabled:opacity-60"
                >
                  {loading && jobType === opt.value ? '⏳ در حال دریافت...' : opt.label}
                </button>
              ))}
              {error && <p className="rounded-xl bg-red-500/20 px-3 py-2 text-xs font-semibold text-red-300">{error}</p>}
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-green-600 shadow-lg shadow-emerald-500/30">
                <span className="text-4xl">✅</span>
              </div>
              <div>
                <p className="text-xl font-black text-white">دانلود شروع شد!</p>
                <p className="mt-2 text-sm text-purple-200">
                  کتابچه رایگان «۱۰ باور کلیدی ثروت‌سازی» روی دستگاه شما ذخیره می‌شود.
                </p>
              </div>
              <a
                href="/booklet.pdf"
                download="کتابچه_ثروتسازی.pdf"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-l from-amber-500 to-yellow-400 px-4 py-3.5 text-sm font-black text-gray-900 shadow-lg shadow-amber-500/30 transition hover:opacity-90"
              >
                📥 دانلود مجدد کتابچه
              </a>
              <a
                href="/"
                className="block text-sm text-purple-300 underline underline-offset-2 hover:text-white"
              >
                بازگشت به سایت
              </a>
            </div>
          )}

        </div>

        {/* Footer note */}
        {step !== 'success' && (
          <p className="mt-5 text-center text-xs text-purple-300/60">
            اطلاعات شما نزد ما محفوظ است و هرگز به اشخاص ثالث منتقل نمی‌شود.
          </p>
        )}
      </div>
    </div>
  );
}
